import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "动漫分类", to: "/category/all" },
  { label: "搜索", to: "/search" },
  { label: "关于我们", to: "/about" },
];

export default function Header() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const goSearch = (value: string) => {
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setDrawerOpen(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    goSearch(keyword);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/90 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex h-16 items-center justify-between gap-4 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50 text-xl">
              <i className="ri-flower-fill"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg font-bold tracking-wide text-foreground-950">樱花动漫</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-primary-500">
                Sakura Anime
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors"
              role="search"
            >
              <i className="ri-search-line text-foreground-400 text-base"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索动漫名称、类型…"
                aria-label="搜索动漫"
                className="w-36 lg:w-52 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="flex items-center gap-1 md:hidden">
            <Link
              to="/search"
              aria-label="搜索"
              className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 text-xl"
            >
              <i className="ri-search-line"></i>
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setDrawerOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 text-xl cursor-pointer"
            >
              <i className="ri-menu-2-line"></i>
            </button>
          </div>
        </div>
      </div>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <aside className="absolute right-0 top-0 h-full w-72 max-w-[80%] bg-background-50 p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <span className="text-base font-bold text-foreground-950">导航菜单</span>
              <button
                type="button"
                aria-label="关闭菜单"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 text-xl cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-5 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索动漫…"
                aria-label="搜索动漫"
                className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
              <button type="submit" className="text-primary-600 text-sm font-medium cursor-pointer">
                搜索
              </button>
            </form>

            <nav className="mt-4 flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-50 text-primary-600"
                        : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="mt-6 rounded-lg bg-secondary-50 p-4 text-xs leading-relaxed text-secondary-900">
              本站内容均为演示数据，版权归原作品方所有，仅供产品展示使用。
            </div>
          </aside>
        </div>
      ) : null}
    </header>
  );
}